import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import type {ProductAnnouncementProps} from '@github-ui/product-announcement/components/ProductAnnouncement'
import type {
  ProductAnnouncementBannerEvent,
  ProductAnnouncementModalEvent,
} from '@github-ui/product-announcement/components/ProductAnnouncement.types'

export const getProductAnnouncementId = ({
  needsOnboarding = false,
}: {
  needsOnboarding?: boolean
} = {}): ProductAnnouncementProps['announcementId'] | '' => {
  if (needsOnboarding) return 'ccaOnboarding'
  if (isFeatureEnabled('hyperspace_nudges_universe25')) return 'universe25'

  return ''
}

export const handleProductAnnouncementBannerTracking = (
  productAnnouncementId: ReturnType<typeof getProductAnnouncementId>,
  event: ProductAnnouncementBannerEvent,
) => {
  if (productAnnouncementId === 'universe25') {
    handleUniverse25BannerTracking(event)
  }
}

export const handleProductAnnouncementModalTracking = (
  productAnnouncementId: ReturnType<typeof getProductAnnouncementId>,
  event: ProductAnnouncementModalEvent,
  data?: Record<string, unknown>,
) => {
  if (productAnnouncementId === 'universe25') {
    handleUniverse25ModalTracking(event, data)
  }
}

// `universe25` tracking ---
const handleUniverse25BannerTracking = (event: ProductAnnouncementBannerEvent) => {
  switch (event) {
    case 'displayed':
      sendEvent('view', {
        category: 'universe_banner',
        action: 'banner_view',
        label: 'banner_universe_loggedIn',
      })
      break
    case 'clicked':
      sendEvent('click', {
        category: 'universe_banner',
        action: 'banner_click_openModal',
        label: 'banner_universe_loggedIn',
      })
      break
    case 'dismissed':
      sendEvent('click', {
        category: 'universe_banner',
        action: 'banner_dismiss',
        label: 'banner_universe_loggedIn',
      })
      break
  }
}

const handleUniverse25ModalTracking = (event: ProductAnnouncementModalEvent, data?: Record<string, unknown>) => {
  switch (event) {
    case 'opened':
      sendEvent('view', {
        category: 'universe_modal',
        action: 'modal_view',
        label: 'modal_universe_loggedIn',
      })
      break
    case 'closed':
      sendEvent('click', {
        category: 'universe_modal',
        action: 'modal_close',
        label: 'modal_universe_loggedIn',
      })
      break
    case 'tab_clicked':
      sendEvent('click', {
        category: 'universe_modal',
        action: 'modal_nav_click',
        label: 'modal_universe_loggedIn',
        navItem: data?.tabName as string,
      })
      break
    case 'main_cta1_clicked':
      sendEvent('click', {
        category: 'universe_modal',
        action: 'modal_primary_cta1_left_click',
        label: 'modal_universe_loggedIn',
      })
      break
    case 'main_cta2_clicked':
      sendEvent('click', {
        category: 'universe_modal',
        action: 'modal_primary_cta2_left_click',
        label: 'modal_universe_loggedIn',
      })
      break
    case 'see_all_clicked':
      sendEvent('click', {
        category: 'universe_modal',
        action: 'modal_seeAllAnnouncements_left_click',
        label: 'modal_universe_loggedIn',
      })
      break
    case 'tab_cta1_clicked':
      sendEvent('click', {
        category: 'universe_modal',
        action: 'modal_primary_cta_right_click',
        label: 'modal_universe_loggedIn',
        navItem: data?.tabName as string,
        ctaType: 'primary_right',
        ctaUrl: data?.url as string,
        userStatus: 'logged_in',
        positionIndex: data?.tabIndex as number,
      })
      break
    case 'tab_cta2_clicked':
      sendEvent('click', {
        category: 'universe_modal',
        action: 'modal_secondary_cta_right_click',
        label: 'modal_universe_loggedIn',
        navItem: data?.tabName as string,
        ctaType: 'secondary_right',
        ctaUrl: data?.url as string,
        userStatus: 'logged_in',
        positionIndex: data?.tabIndex as number,
      })
      break
    case 'tab_link_clicked':
      sendEvent('click', {
        category: 'universe_modal',
        action: 'modal_link_right_click',
        label: 'modal_universe_loggedIn',
        navItem: data?.tabName as string,
        ctaType: 'learn_more_right',
        ctaUrl: data?.url as string,
        userStatus: 'logged_in',
        positionIndex: data?.tabIndex as number,
      })
      break
    case 'tab_description_link_clicked':
      sendEvent('click', {
        category: 'universe_modal',
        action: 'modal_description_link_right_click',
        label: 'modal_universe_loggedIn',
        navItem: data?.tabName as string,
        ctaType: 'link',
        ctaUrl: data?.url as string,
        userStatus: 'logged_in',
        positionIndex: data?.tabIndex as number,
      })
      break
    case 'tab_dropdown_opened':
      sendEvent('click', {
        category: 'universe_modal',
        action: 'modal_dropdown_opened',
        label: 'modal_universe_loggedIn',
        navItem: data?.tabName as string,
        userStatus: 'logged_in',
        positionIndex: data?.tabIndex as number,
      })
      break
    case 'tab_dropdown_item_clicked':
      sendEvent('click', {
        category: 'universe_modal',
        action: 'modal_dropdown_item_clicked',
        label: 'modal_universe_loggedIn',
        navItem: data?.tabName as string,
        ctaType: 'link',
        ctaUrl: data?.url as string,
        userStatus: 'logged_in',
        positionIndex: data?.tabIndex as number,
      })
      break
  }
}
